"use client";

import { c as _c } from "react/compiler-runtime";
import { CustomImage } from "@repo/design-system/components/ui/custom-image";
import { CustomLink } from "@repo/design-system/components/ui/custom-link";
import { useFilterSearch } from "@repo/design-system/hooks/use-filter-search";
import { useLocale } from "@repo/design-system/hooks/use-locale";
import { type ResponsiveColumns, useResponsiveColumns } from "@repo/design-system/hooks/use-responsive-columns";
import { matchLocale } from "@repo/design-system/lib/middleware/handle-locale";
import { cn } from "@repo/design-system/utils/cn";
import { getJobAdUrlFromData } from "@repo/design-system/utils/helpers";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useMemo } from "react";
import { Button } from "~/components/ui/button-eon";
import { ItemContent, ItemMedia, ItemTitle } from "~/components/ui/item-eon";
import { LocationDisplay } from "~/components/ui/location-display-eon";
import { toSimilarJob } from "~/lib/utils/to-similar-job";
import { i18n } from "~/setup/locales";
import { useLanguage } from "~/translations/loader/use-language";
import type { JobExcerpt } from "~/types/job";
import { WatchJobButton } from "../ui/watch-job-button-eon";
import { JobListSkeleton } from "./skeletons-eon";
export function Joblist({
  hideWatchColumn = false
}: {
  /** The embedded widget joblist has no watch column (watched jobs inside the
   * iframe would not be visible on the actual jobboard) */
  hideWatchColumn?: boolean;
}) {
  "use no memo";

  const t = useLanguage();
  const {
    data: jobs,
    isLoading,
    isFetchingNextPage,
    fetchNextPage
  } = useFilterSearch();
  const allJobs = useMemo(() => (jobs?.pages.flatMap(page => page.jobs) || []) as JobExcerpt[], [jobs]);
  const totalHits = jobs?.pages[0].totalHits || 0;
  const columnDef: ResponsiveColumns<JobExcerpt> = [{
    header: t.joblist_header1,
    cell: ({
      row: {
        original: {
          data
        }
      }
    }) => <CustomLink blank className="flex w-full flex-col" href={data.jobBoard_link ? data.jobBoard_link : getJobAdUrlFromData(data)} locale={matchLocale([data.language], i18n.locales, i18n.default)} passParams>
          <ItemContent className="gap-0">
            <ItemTitle className="font-bold text-primary text-xl" lang={data.language?.toLowerCase()}>
              {data.title}
            </ItemTitle>
            {data.company && <p className="text-base text-foreground">{data.company}</p>}
          </ItemContent>
        </CustomLink>
  }, {
    hideOn: ["sm", "md"],
    header: t.joblist_header2,
    cell: ({
      row
    }) => {
      const jobLocale = matchLocale([row.original.data.language], i18n.locales, i18n.default);
      return <CustomLink blank className="block h-full w-full" href={row.original.data.jobBoard_link || getJobAdUrlFromData(row.original.data)} locale={jobLocale} passParams tabIndex={-1}>
            <LocationDisplay job={row.original} />
          </CustomLink>;
    }
  }, {
    hideOn: ["sm", "md"],
    id: "Company",
    cell: ({
      row: row_0
    }) => {
      const jobLocale_0 = matchLocale([row_0.original.data.language], i18n.locales, i18n.default);
      return <CustomLink blank className="relative flex h-full w-full items-start justify-center" href={row_0.original.data.jobBoard_link || getJobAdUrlFromData(row_0.original.data)} locale={jobLocale_0} passParams tabIndex={-1}>
            <ItemMedia className="h-14 w-[147px] shrink-0">
              <CustomImage alt={row_0.original.data.company || "Company logo"} className="h-full w-full object-contain opacity-70 grayscale transition-all duration-200 group-hover:opacity-100 group-hover:grayscale-0" height={56} src={`images/logo/joblistLogos/${row_0.original.data.joblistLogo}`} width={147} />
            </ItemMedia>
          </CustomLink>;
    }
  }, ...(hideWatchColumn ? [] : [{
    header: t.joblist_header4,
    id: t.joblist_header4,
    cell: ({
      row: row_1
    }) => <div className="flex w-full justify-center">
                <WatchJobButton jobData={toSimilarJob(row_1.original.data)} variant="icon" />
              </div>
  }] satisfies ResponsiveColumns<JobExcerpt>)];
  const gridCols = hideWatchColumn ? "grid-cols-1 lg:grid-cols-[45%_30%_25%]" : "grid-cols-joblist-small md:grid-cols-joblist-medium lg:grid-cols-joblist-large";
  const columns = useResponsiveColumns(columnDef);
  const table = useReactTable({
    data: allJobs,
    columns,
    getCoreRowModel: getCoreRowModel()
  });
  if (isLoading) {
    return <JobListSkeleton />;
  }
  return <section className="outer-container pb-6 text-lg md:pb-20">
      <div className="inner-container">
        <table className="my-8 w-full table-fixed border-collapse">
          <thead className="border-gray-400 border-b">
            {table.getHeaderGroups().map(headerGroup => <tr className={cn("grid items-center justify-items-start py-6 pr-2 sm:pr-0 md:pl-4", gridCols)} key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => <th className={cn(index === headerGroup.headers.length - 1 && "flex w-full justify-center hover:no-underline", index <= 2 && "hover:no-underline")} key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>)}
              </tr>)}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row_2 => <tr className={cn("group grid justify-items-start border-gray-200 border-b py-6 pr-4 text-foreground transition-colors duration-250 hover:bg-gray-200 sm:pr-0 md:pl-4", gridCols)} key={row_2.id}>
                {row_2.getVisibleCells().map(cell => <td className="w-full pr-1" key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>)}
              </tr>)}
          </tbody>
        </table>
        <div className="col-span-full flex w-full items-center justify-center">
          <LoadMoreButton fetchNextPage={fetchNextPage} isFetchingNextPage={isFetchingNextPage} jobsPerPage={jobs?.pages[0].jobsPerPage ?? 0} pagesLength={jobs?.pages.length ?? 0} totalHits={totalHits} />
        </div>
      </div>
    </section>;
}
function LoadMoreButton(t0) {
  const $ = _c(11);
  const {
    isFetchingNextPage,
    jobsPerPage,
    pagesLength,
    totalHits,
    fetchNextPage
  } = t0;
  const t = useLanguage();
  const locale = useLocale();
  const loadedJobs = Math.min(Number(pagesLength) * Number(jobsPerPage), Number(totalHits));
  const hasMoreJobs = loadedJobs < totalHits;
  if (!hasMoreJobs) {
    return null;
  }
  let t1;
  if ($[0] !== isFetchingNextPage || $[1] !== loadedJobs || $[2] !== locale || $[3] !== t.joblist_loadMore1 || $[4] !== t.joblist_loadMore2 || $[5] !== totalHits) {
    t1 = isFetchingNextPage ? t.joblist_loadMore1 || "Loading..." : `${t.joblist_loadMore1} (${loadedJobs.toLocaleString(locale)} ${t.joblist_loadMore2} ${totalHits.toLocaleString(locale)})`;
    $[0] = isFetchingNextPage;
    $[1] = loadedJobs;
    $[2] = locale;
    $[3] = t.joblist_loadMore1;
    $[4] = t.joblist_loadMore2;
    $[5] = totalHits;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== fetchNextPage || $[8] !== isFetchingNextPage || $[9] !== t1) {
    t2 = <Button disabled={isFetchingNextPage} onClick={fetchNextPage}>{t1}</Button>;
    $[7] = fetchNextPage;
    $[8] = isFetchingNextPage;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}